// src/components/OrderDetail.js
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import {
  doc,
  onSnapshot,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  writeBatch,
  increment,
  serverTimestamp,
  deleteField,
  arrayRemove // <-- 1. Aggiunto arrayRemove
  // getDoc e addDoc rimossi perché non usati
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

// --- IMPORT LIBRERIA EXCEL ---
import * as XLSX from 'xlsx'; 
// --- FINE IMPORT LIBRERIA EXCEL ---

// --- IMPORT MUI ---
import {
  Box,
  Typography, 
  IconButton,
  CircularProgress, Paper, Divider, Button, TextField, List,
  ListItem, ListItemText, ListItemIcon, 
  Chip, Autocomplete, Alert,
  Tooltip,
  // --- Aggiungi componenti Modale ---
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
  // --- Fine Aggiunta ---
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import FileDownloadIcon from '@mui/icons-material/FileDownload'; 
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'; // <-- 2. Icona Sostitutiva per Parziale
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// --- MODIFICA: Helper (con t() e lingua) ---
const formatDate = (date, lang) => { 
    if(!date)return'N/A'; 
    const d=date.toDate?date.toDate():new Date(date); 
    const locale = lang === 'it' ? 'it-IT' : 'en-GB';
    return d.toLocaleDateString(locale,{day:'2-digit',month:'2-digit',year:'numeric'}); 
};
const getStatusChipColor = (status) => { 
  if (status === 'Received') return 'success';
  if (status === 'Partially Received') return 'info'; // <-- 3. Colore per Parziale
  if (status === 'Shipped') return 'info';
  if (status === 'Ordered') return 'primary'; 
  return 'warning'; // Pending
};
// Funzione helper per tradurre lo stato
const translateStatus = (status, t) => {
  const key = `status_${status.toLowerCase()}`;
  const translated = t(key);
  return translated === key ? status : translated;
}
// --- FINE MODIFICA ---

// --- Aggiungi Modale Ricezione Merce ---
const ReceiveOrderModal = ({ open, onClose, orderItems, onConfirmReceive, t }) => {
  const [receivedQuantities, setReceivedQuantities] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const initialQtys = {};
      orderItems.forEach(item => {
        const qtyPending = (item.quantityOrdered || item.quantity) - (item.quantityReceived || 0);
        // Pre-compila con la quantità mancante
        initialQtys[item.id] = qtyPending < 0 ? 0 : qtyPending;
      });
      setReceivedQuantities(initialQtys);
    }
  }, [open, orderItems]);

  const handleChange = (itemId, value) => {
    const qty = Number(value);
    setReceivedQuantities(prev => ({
      ...prev,
      [itemId]: qty < 0 ? 0 : qty // Impedisci numeri negativi
    }));
  };

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      await onConfirmReceive(receivedQuantities);
      onClose();
    } catch (error) {
      console.error("Error confirming reception:", error);
      alert(t('receive_modal.error_generic')); // Aggiungi 'receive_modal.error_generic'
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('receive_modal.title')}</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>{t('receive_modal.subtitle')}</Typography>
        <List>
          {orderItems.map(item => {
            const ordered = item.quantityOrdered || item.quantity; // Supporta vecchio formato
            const received = item.quantityReceived || 0;
            const pending = ordered - received;
            
            return (
              <ListItem key={item.id} divider>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <ListItemText 
                      primary={item.name} 
                      secondary={`${t('receive_modal.ordered')}: ${ordered} | ${t('receive_modal.received')}: ${received}`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('receive_modal.receiving_now')}
                      type="number"
                      size="small"
                      fullWidth
                      value={receivedQuantities[item.id] || 0}
                      onChange={(e) => handleChange(item.id, e.target.value)}
                      inputProps={{ min: 0, max: pending }} // Non può ricevere più del dovuto
                    />
                  </Grid>
                </Grid>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>{t('modal_general_cancel')}</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={isSaving}>
          {t('receive_modal.confirm_button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// --- Fine Modale ---

function OrderDetail({ teamId, userRole }) {
  // --- MODIFICA: Inizializza t() ---
  const { t, i18n } = useTranslation('order');
  const currentLang = i18n.language;
  // --- FINE MODIFICA ---

  const { orderId } = useParams();
  const navigate = useNavigate();

  // Stati (invariati)
  const [order, setOrder] = useState(null);
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [isLoadingInventory, setIsLoadingInventory] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(1);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isReceiving, setIsReceiving] = useState(false);
  const canManage = userRole === 'admin' || userRole === 'Engineer' || userRole === 'Magazziniere' || userRole === 'Meccanico';

  // --- Stato per il modale ---
  const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
  // --- Fine Aggiunta ---

  // useEffects (invariati)
  useEffect(() => {
    if (!teamId || !orderId) return;
    setIsLoadingOrder(true);
    const orderRef = doc(db, "orders", orderId);
    const unsubscribe = onSnapshot(orderRef, (docSnap) => {
      if (docSnap.exists() && docSnap.data().teamId === teamId) {
        setOrder({ id: docSnap.id, ...docSnap.data() });
      } else {
        console.error("Order not found or access denied.");
        setOrder(null);
      }
      setIsLoadingOrder(false);
    }, (err) => { console.error("Error loading order:", err); setIsLoadingOrder(false); });
    return () => unsubscribe();
  }, [orderId, teamId]);
  
  useEffect(() => {
    if (!teamId) { setIsLoadingInventory(false); return; }
    setIsLoadingInventory(true);
    const q = query(collection(db, "inventory"), where("teamId", "==", teamId), orderBy("name", "asc"));
    const unsub = onSnapshot(q, (qs) => {
      setInventory(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingInventory(false);
    }, (err) => { console.error("Error loading inventory:", err); setIsLoadingInventory(false); });
    return () => unsub();
  }, [teamId]);

  // --- Aggiorna `orderItems` e `totalCost` per nuovo formato ---
  const orderItems = useMemo(() => {
    if (!order || !order.itemsMap) {
      // Supporto per la vecchia struttura (array)
      return order?.items || [];
    }
    // Converte la mappa in array per la UI
    return Object.values(order.itemsMap);
  }, [order]);

  const totalCost = useMemo(() => {
    return orderItems.reduce((acc, item) => {
      const qty = item.quantityOrdered || item.quantity; // Supporta entrambi
      return acc + (qty * (item.prezzoAcquisto || 0));
    }, 0);
  }, [orderItems]);
  // --- Fine Aggiunta ---

  // --- MODIFICA: Funzioni Handler (con t() per alert) ---
  const handleAddItemToOrder = async () => {
    if (!selectedItem || itemQuantity <= 0) { alert(t('item_add_error_select')); return; }
    
    // --- Modifica per usare `itemsMap` ---
    const itemId = selectedItem.id;
    if (order.itemsMap && order.itemsMap[itemId]) {
      alert(t('item_add_error_exists')); 
      return; 
    }
    // Supporto fallback per vecchio formato
    if (!order.itemsMap && order.items && order.items.some(item => item.id === selectedItem.id)) {
      alert(t('item_add_error_exists')); 
      return; 
    }
    
    setIsAddingItem(true);
    const orderRef = doc(db, "orders", orderId);
    
    const itemToAdd = { 
      id: selectedItem.id, 
      name: selectedItem.name, 
      code: selectedItem.code || '', 
      quantityOrdered: Number(itemQuantity), // <-- Nuovo campo
      quantityReceived: 0, // <-- Nuovo campo
      prezzoAcquisto: selectedItem.prezzoAcquisto || 0 
    };

    try {
      // Se l'ordine usa ancora il vecchio array `items`, convertilo.
      if (!order.itemsMap) {
        const newItemsMap = {};
        (order.items || []).forEach(item => {
          newItemsMap[item.id] = {
            ...item,
            quantityOrdered: item.quantity, // Converti
            quantityReceived: 0
          };
          delete newItemsMap[item.id].quantity; // Rimuovi vecchio campo
        });
        
        newItemsMap[itemId] = itemToAdd;
        
        await updateDoc(orderRef, {
          itemsMap: newItemsMap, // <-- Salva la nuova mappa
          items: deleteField() // <-- Rimuovi il vecchio array
        });
        
      } else {
        // Altrimenti, aggiungi atomicamente alla mappa
        await updateDoc(orderRef, {
          [`itemsMap.${itemId}`]: itemToAdd
        });
      }
      
      setSelectedItem(null); setItemQuantity(1);
    } catch (error) { 
      console.error("Error adding item:", error); 
    }
    setIsAddingItem(false);
    // --- Fine Modifica ---
  };
  
  const handleRemoveItemFromOrder = async (itemToRemove) => {
     if (window.confirm(t('item_remove_confirm', { name: itemToRemove.name }))) {
        const orderRef = doc(db, "orders", orderId);
        try { 
          // --- Modifica per `itemsMap` ---
          if (order.itemsMap) {
             await updateDoc(orderRef, {
              [`itemsMap.${itemToRemove.id}`]: deleteField()
            });
          } else {
            // Logica fallback per vecchio formato
            await updateDoc(orderRef, { items: arrayRemove(itemToRemove) });
          }
          // --- Fine Modifica ---
        }
        catch (error) { 
          console.error("Error removing item:", error); 
          alert(t('item_remove_error'));
        }
     }
  };

  const handleSetStatus = async (newStatus) => {
    const orderRef = doc(db, "orders", orderId);
    try {
      await updateDoc(orderRef, {
        status: newStatus
      });
    } catch (error) {
      console.error(`Error setting status to ${newStatus}:`, error);
      alert(t('status_update_error'));
    }
  };

  // --- SOSTITUISCI `handleReceiveOrder` ---
  const handleConfirmReceive = async (receivedQuantities) => {
    const itemsToUpdate = orderItems;
    if (itemsToUpdate.length === 0) return;

    setIsReceiving(true);
    try {
      const batch = writeBatch(db);
      const orderRef = doc(db, "orders", orderId);
      
      let totalOrdered = 0;
      let newTotalReceived = 0;
      const updates = {}; // Aggiornamenti per itemsMap

      for (const item of itemsToUpdate) {
        const qtyToAdd = Number(receivedQuantities[item.id]) || 0;
        if (qtyToAdd > 0) {
          const inventoryItemRef = doc(db, "inventory", item.id);
          batch.update(inventoryItemRef, { 
            quantity: increment(qtyToAdd)
          });
          
          const currentReceived = item.quantityReceived || 0;
          updates[`itemsMap.${item.id}.quantityReceived`] = currentReceived + qtyToAdd;
        }
        
        totalOrdered += (item.quantityOrdered || item.quantity);
        newTotalReceived += (item.quantityReceived || 0) + (qtyToAdd > 0 ? qtyToAdd : 0); // Usa il valore aggiornato se aggiunto
      }
      
      // Determina il nuovo stato
      let newStatus = 'Shipped'; // Default
      if (newTotalReceived >= totalOrdered) {
        newStatus = 'Received';
      } else if (newTotalReceived > 0) {
        newStatus = 'Partially Received';
      } else {
        newStatus = order.status; // Rimane invariato se non si riceve nulla
      }

      batch.update(orderRef, {
        ...updates, // Applica gli aggiornamenti a itemsMap
        status: newStatus,
        receivedAt: serverTimestamp() // Aggiorna sempre la data
      });

      await batch.commit();
      setIsReceiveModalOpen(false); // Chiudi il modale
      
    } catch (error) {
      console.error("Error receiving goods:", error); 
      alert(t('receive_error_generic')); 
      throw error; // Rilancia l'errore per il modale
    } finally {
      setIsReceiving(false);
    }
  };
  // --- Fine Sostituzione ---
  
  const handleToggleArchive = async (archiveState) => {
    const newState = archiveState;
    const actionText = newState ? t('archive_action') : t('unarchive_action');
    if (!window.confirm(t('archive_confirm', { action: actionText }))) return;
    
    const orderRef = doc(db, "orders", orderId);
    try {
      await updateDoc(orderRef, { isArchived: newState });
      if (newState === true) {
        navigate('/order-manager');
      }
    } catch (error) {
      console.error(`Error during ${actionText} the order:`, error);
      alert(t('archive_error'));
    }
  };

  const handleExportToExcel = () => {
    if (!order || orderItems.length === 0) {
      alert(t('export_error_empty'));
      return;
    }

    const headerData = [
      [t('export_header_main')],
      [],
      [t('export_header_supplier'), order.supplierName],
      [t('export_header_date'), formatDate(order.orderDate, currentLang)],
      [t('export_header_status'), translateStatus(order.status, t)],
      [t('export_header_notes'), order.notes || 'N/A'],
      [t('export_header_id'), order.id],
      [], 
      [t('export_header_items')]
    ];

    const itemHeaders = [
      t('export_col_qty_ordered'), // <-- Modificato
      t('export_col_qty_received'), // <-- Aggiunto
      t('export_col_code'), 
      t('export_col_desc'), 
      t('export_col_unit_price'), 
      t('export_col_total')
    ];
    
    let totalOrderCost = 0;
    
    const itemRows = orderItems.map(item => {
      const ordered = item.quantityOrdered || item.quantity;
      const received = item.quantityReceived || 0;
      const prezzo = item.prezzoAcquisto || 0;
      const subtotale = ordered * prezzo;
      totalOrderCost += subtotale;
      return [
        ordered,
        received,
        item.code || '',
        item.name,
        prezzo.toFixed(2),
        subtotale.toFixed(2)
      ];
    });
    
    const footerData = [
      [],
      ["", "", "", "", t('export_footer_total'), totalOrderCost.toFixed(2)]
    ];

    const dataForSheet = [...headerData, itemHeaders, ...itemRows, ...footerData];
    
    // --- 4. Correzione Errore Export ---
    const ws = XLSX.utils.aoa_to_sheet(dataForSheet);
    ws['!cols'] = [ { wch: 10 }, { wch: 10 }, { wch: 25 }, { wch: 40 }, { wch: 15 }, { wch: 15 } ]; // Aggiunta colonna
    // --- Fine Correzione ---

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ordine");
    const filename = `Ordine_${order.supplierName.replace(/\s/g, '_')}_${formatDate(order.orderDate, currentLang).replace(/\//g, '-')}.xlsx`;
    XLSX.writeFile(wb, filename);
  };
  // --- FINE MODIFICA ---


  if (isLoadingOrder) {
    return ( <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box> );
  }
  if (!order) {
    return ( <Box><Typography color="error">Order not found.</Typography></Box> );
  }
  
  const isOrderEditable = (order.status === 'Pending' || order.status === 'Ordered') && !order.isArchived && canManage;
  const isOrderReadOnly = (!isOrderEditable) && canManage;

  // --- Logica per abilitare il pulsante Ricevi ---
  const canReceiveOrder = (
    order.status === 'Shipped' || 
    order.status === 'Partially Received'
  ) && !order.isArchived && canManage;
  // --- Fine Aggiunta ---

  // --- RENDER (MODIFICATO con t()) ---
  return (
    <>
      {/* 1. Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          backgroundColor: 'background.paper', 
          border: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton color="inherit" component={RouterLink} to="/order-manager" aria-label={t('back_button_aria')} sx={{ mr: 2, p: 0.5 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              {t('detail_title', { name: order.supplierName })}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {formatDate(order.orderDate, currentLang)}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          

          {canManage && !order.isArchived && orderItems.length > 0 && (
            <>
              {order.status === 'Pending' && (
                <Button variant="contained" color="primary" onClick={() => handleSetStatus('Ordered')} size="large">
                  {t('place_order_button')}
                </Button>
              )}
              {order.status === 'Ordered' && (
                <Button variant="contained" color="info" onClick={() => handleSetStatus('Shipped')} size="large">
                  {t('mark_shipped_button')}
                </Button>
              )}
              {/* --- Sostituisci il pulsante "Mark Received" --- */}
              {canReceiveOrder && (
                <Button 
                  variant="contained" 
                  color="success" 
                  startIcon={<CheckCircleIcon />} 
                  onClick={() => setIsReceiveModalOpen(true)} // <-- Apre il modale
                  disabled={isReceiving} 
                  size="large"
                >
                  {t('receive_button')} {/* Aggiungi 'receive_button' */}
                </Button>
              )}
              {/* --- Fine Sostituzione --- */}
            </>
          )}

          {canManage && (order.status === 'Received' || order.status === 'Partially Received') && !order.isArchived && (
            <Tooltip title={t('archive_tooltip')}>
              <IconButton color="inherit" onClick={() => handleToggleArchive(true)} size="large">
                <ArchiveIcon />
              </IconButton>
            </Tooltip>
          )}
          {canManage && order.isArchived && (
            <Tooltip title={t('unarchive_tooltip')}>
              <IconButton color="inherit" onClick={() => handleToggleArchive(false)} size="large">
                <UnarchiveIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Paper>

      {order.isArchived && <Alert severity="warning" sx={{mb: 2}}>{t('archived_alert')}</Alert>}

      {/* Dettagli Ordine */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: 3,
          border: '1px solid', 
          borderColor: 'divider', 
          backgroundColor: 'background.paper' 
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h5" gutterBottom>{order.supplierName}</Typography>
            <Typography variant="body1">{t('order_date')} {formatDate(order.orderDate, currentLang)}</Typography>
            <Typography variant="body1">{t('notes')} {order.notes || 'N/A'}</Typography>
            <Typography variant="h6" sx={{ mt: 1 }}>
              {t('total_label')} €{totalCost.toFixed(2)}
            </Typography>
          </Box>
          <Chip 
            label={translateStatus(order.status, t)} 
            color={getStatusChipColor(order.status)} 
            // Usa l'icona corretta per lo stato parziale
            icon={order.status === 'Partially Received' ? <MoreHorizIcon /> : (order.status === 'Received' ? <CheckCircleIcon /> : undefined)}
            sx={{ fontSize: '1.1rem', p: 2 }} 
          />
        </Box>
      </Paper>
      
      {/* Articoli nell'Ordine */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 },
          border: '1px solid', 
          borderColor: 'divider', 
          backgroundColor: 'background.paper' 
        }}
      >
        <Typography variant="h5" gutterBottom>{t('items_title')}</Typography>
        
        {isOrderEditable && (
          <Box component="form" sx={{ mb: 3 }} onSubmit={(e) => { e.preventDefault(); handleAddItemToOrder(); }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, alignItems: 'center' }}>
              <Autocomplete
                options={inventory}
                getOptionLabel={(option) => `${option.name} ${option.code ? `(${option.code})` : ''} (Avail: ${option.quantity})`}
                value={selectedItem}
                onChange={(event, newValue) => setSelectedItem(newValue)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => ( <TextField {...params} label={t('item_search')} /> )}
                disabled={isLoadingInventory || isAddingItem}
                fullWidth
                sx={{ flex: 3 }}
              />
              <TextField 
                label={t('item_quantity')}
                type="number" 
                value={itemQuantity}
                onChange={(e) => setItemQuantity(e.target.value)}
                required 
                InputProps={{ inputProps: { min: 1 } }}
                sx={{ flex: 1 }}
              />
              <Button 
                type="submit" 
                variant="contained" 
                disabled={isAddingItem} 
                startIcon={<AddIcon />} 
                fullWidth 
                size="large" 
                sx={{ height: '56px', flex: 1 }}
              >
                {t('item_add_button')}
              </Button>
            </Box>
            <Divider sx={{ my: 3 }} />
          </Box>
        )}
        
        {isOrderReadOnly && !order.isArchived && <Alert severity="info" sx={{mb: 2}}>{t('readonly_alert')}</Alert>}

        {(isLoadingOrder || isLoadingInventory) && <CircularProgress size={24} />}
        {!(isLoadingOrder || isLoadingInventory) && orderItems.length === 0 && (
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{t('empty_items')}</Typography>
        )}
        {!(isLoadingOrder || isLoadingInventory) && orderItems.length > 0 && (
          <List dense>
            {/* --- Modifica Lista Articoli --- */}
            {orderItems.map((item) => {
              const ordered = item.quantityOrdered || item.quantity;
              const received = item.quantityReceived || 0;
              const isPending = received < ordered;
              const prezzo = item.prezzoAcquisto || 0;
              const subtotale = ordered * prezzo;
              
              return (
                <ListItem
                  key={item.id}
                  divider
                  sx={{ 
                    backgroundColor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    mb: 1
                  }}
                  secondaryAction={ isOrderEditable ? ( <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveItemFromOrder(item)}> <DeleteIcon color="error" /> </IconButton> ) : null }
                >
                  <ListItemIcon sx={{ minWidth: 50, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="h6" color={isPending ? 'text.secondary' : 'success.main'}>
                      {received}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      / {ordered}
                    </Typography>
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    secondary={`${t('item_code')} ${item.code || 'N/A'} | ${t('item_price')} €${prezzo.toFixed(2)} | ${t('item_subtotal')} €${subtotale.toFixed(2)}`}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                  {isPending && (order.status === 'Received' || order.status === 'Partially Received') && (
                     <Chip 
                       icon={<WarningAmberIcon />} 
                       label={t('item_status_pending')} 
                       color="warning" 
                       size="small" 
                       sx={{ ml: 2 }}
                     />
                  )}
                  {(!isPending && (order.status === 'Received' || order.status === 'Partially Received')) && (
                     <Chip 
                       icon={<CheckCircleIcon />} 
                       label={t('item_status_received')} 
                       color="success" 
                       size="small" 
                       sx={{ ml: 2 }}
                     />
                  )}
                </ListItem>
              );
            })}
            {/* --- Fine Modifica Lista --- */}
          </List>
        )}
      </Paper>

      {/* --- Aggiungi il modale al render --- */}
      <ReceiveOrderModal
        open={isReceiveModalOpen}
        onClose={() => setIsReceiveModalOpen(false)}
        orderItems={orderItems.filter(item => (item.quantityOrdered || item.quantity) > (item.quantityReceived || 0))} // Mostra solo articoli pendenti
        onConfirmReceive={handleConfirmReceive}
        t={t}
      />
    </>
  );
}

export default OrderDetail;